/**
 * FocusMap Canon: Standardized focus order zones and selectors
 * 
 * Canonical order:
 * 1. Skip links
 * 2. Toolbar (Search → Refresh → Filters → Export → Primary)
 * 3. Filters (if expanded)
 * 4. StatGrid
 * 5. Table (with roving tabindex)
 * 6. Row actions
 * 7. Dialogs/Drawers (with trap focus)
 * 
 * Detail Pages order:
 * 1. Skip links
 * 2. Back button
 * 3. Toolbar (Refresh → Export → Actions)
 * 4. Tabs (Overview → Timeline → Audit Trail) - roving tabindex
 * 5. Overview Card (InfoCard)
 * 6. RelatedTable (roving tabindex)
 * 7. Dialogs/Drawers (with trap focus)
 */

export const FOCUS_ZONES = {
  skipLinks: '[data-skip-link]',
  toolbar: '[role="toolbar"]',
  toolbarSearch: '[role="toolbar"] input[type="search"]',
  toolbarRefresh: '[role="toolbar"] button[aria-label*="Refresh"]',
  toolbarFilters: '[role="toolbar"] button[aria-label*="Filter"]',
  toolbarExport: '[role="toolbar"] button[aria-label*="Export"]',
  toolbarPrimary: '[role="toolbar"] button[aria-label*="Primary"]',
  filters: '[data-filters-panel]',
  statGrid: '[data-stat-grid]',
  table: 'table[role="table"]',
  tableRow: 'tr[data-focusable]',
  tableCell: 'td[data-focusable]',
  rowActions: '[data-row-actions]',
  dialog: '[role="dialog"]',
  drawer: '[role="dialog"][data-drawer]',
  // Detail page specific
  backButton: '[data-detail-back]',
  tabs: '[role="tablist"]',
  tabTrigger: '[role="tab"][data-focusable]',
  overviewCard: '[data-overview-card]',
  relatedTable: '[data-related-table]',
} as const

export const FOCUS_ORDER = [
  FOCUS_ZONES.skipLinks,
  FOCUS_ZONES.toolbarSearch,
  FOCUS_ZONES.toolbarRefresh,
  FOCUS_ZONES.toolbarFilters,
  FOCUS_ZONES.toolbarExport,
  FOCUS_ZONES.toolbarPrimary,
  FOCUS_ZONES.filters,
  FOCUS_ZONES.statGrid,
  FOCUS_ZONES.table,
  FOCUS_ZONES.rowActions,
] as const

export const FOCUS_ORDER_DETAIL = [
  FOCUS_ZONES.skipLinks,
  FOCUS_ZONES.backButton,
  FOCUS_ZONES.toolbarRefresh,
  FOCUS_ZONES.toolbarExport,
  FOCUS_ZONES.toolbarPrimary,
  FOCUS_ZONES.tabs,
  FOCUS_ZONES.overviewCard,
  FOCUS_ZONES.relatedTable,
  FOCUS_ZONES.dialog,
] as const

/**
 * Get next focusable element in canonical order
 */
export function getNextFocusable(current: HTMLElement | null): HTMLElement | null {
  if (!current) return null
  
  const currentZone = FOCUS_ORDER.findIndex(selector => 
    current.matches(selector) || current.closest(selector)
  )
  
  if (currentZone === -1 || currentZone === FOCUS_ORDER.length - 1) {
    return null
  }
  
  const nextSelector = FOCUS_ORDER[currentZone + 1]
  if (!nextSelector) return null
  
  const next = document.querySelector<HTMLElement>(nextSelector)
  return next ?? null
}

/**
 * Get previous focusable element in canonical order
 */
export function getPreviousFocusable(current: HTMLElement | null): HTMLElement | null {
  if (!current) return null
  
  const currentZone = FOCUS_ORDER.findIndex(selector => 
    current.matches(selector) || current.closest(selector)
  )
  
  if (currentZone <= 0) {
    return null
  }
  
  const prevSelector = FOCUS_ORDER[currentZone - 1]
  if (!prevSelector) return null
  
  const prev = document.querySelector<HTMLElement>(prevSelector)
  return prev ?? null
}

export function getFocusAdvance(container: HTMLElement, order: readonly string[]) {
  const observer = new MutationObserver(() => {})
  observer.observe(container, { subtree: true, childList: true })

  const listener = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') return
    const active = document.activeElement as HTMLElement | null
    if (!active) return

    const getElement = (selector: string) =>
      container.querySelector<HTMLElement>(selector)

    const currentIndex = order.findIndex((selector) => active.matches(selector) || active.closest(selector))
    if (currentIndex === -1) return

    if (!event.shiftKey) {
      const nextSelector = order[currentIndex + 1]
      const next = nextSelector ? getElement(nextSelector) : null
      if (next) {
        event.preventDefault()
        next.focus()
      }
    } else {
      const prevSelector = order[currentIndex - 1]
      const prev = prevSelector ? getElement(prevSelector) : null
      if (prev) {
        event.preventDefault()
        prev.focus()
      }
    }
  }

  container.addEventListener('keydown', listener)

  return () => {
    observer.disconnect()
    container.removeEventListener('keydown', listener)
  }
}

